import { useEffect, useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  initializeAbly,
  addConnectionListener,
  getConnectionState,
  setQueryClient,
  subscribeToChat,
  unsubscribeFromChat,
} from "@/lib/ably";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/authStore";
import { usePingStore } from "@/stores/ping-store";
import {
  markChatAsReadInCache,
  setCurrentViewingChat,
  initializeChatUnreadCounts,
} from "@/lib/chat-cache-manager";
import type { SupportChat } from "@/types";

// Track which chats are currently subscribed for the chat list
const activeChatListSubscriptions = new Set<string>();

// Subscribe to multiple chats for chat list updates using centralized subscription
const subscribeToChatsForList = (chatIds: string[]) => {
  // Unsubscribe from chats that are no longer in the list
  const chatsToUnsubscribe = Array.from(activeChatListSubscriptions).filter(
    (chatId) => !chatIds.includes(chatId)
  );

  chatsToUnsubscribe.forEach((chatId) => {
    unsubscribeFromChat(chatId, "chatlist");
    activeChatListSubscriptions.delete(chatId);
  });

  // Subscribe to new chats
  chatIds.forEach((chatId) => {
    if (!activeChatListSubscriptions.has(chatId)) {
      try {
        // Use centralized subscription with 'chatlist' context
        subscribeToChat(chatId, "chatlist");
        activeChatListSubscriptions.add(chatId);
      } catch (error) {
        console.error(
          `Error subscribing to chat ${chatId} for list updates:`,
          error
        );
      }
    }
  });
};

// Unsubscribe from all chat list subscriptions
const unsubscribeFromAllChatsForList = () => {
  activeChatListSubscriptions.forEach((chatId) => {
    unsubscribeFromChat(chatId, "chatlist");
  });
  activeChatListSubscriptions.clear();
};

// Hook for managing real-time chat list updates
export const useRealtimeChats = (
  chats: SupportChat[],
  isInactiveTab: boolean = false
) => {
  const { isAuthenticated } = useAuthStore();
  const { selectedChatId } = usePingStore();
  const [connectionState, setConnectionState] = useState(getConnectionState());
  const queryClient = useQueryClient();

  // Extract chat IDs from the chats array and initialize cache
  const chatIds = useMemo(() => {
    const ids = chats.map((chat) => chat.id);
    if (chats.length > 0) {
      initializeChatUnreadCounts(chats);
    }
    return ids;
  }, [chats]);

  // Set the queryClient in Ably module on first use
  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  // Monitor connection state
  useEffect(() => {
    const removeListener = addConnectionListener(setConnectionState);
    return () => {
      removeListener();
    };
  }, []);

  // Initialize Ably connection on mount
  useEffect(() => {
    if (isAuthenticated) {
      initializeAbly().catch((error) => {
        console.error("Failed to initialize Ably:", error);
      });
    }
  }, [isAuthenticated]);

  // Subscribe to multiple chats when connection is ready and chats are available
  useEffect(() => {
    if (
      !isAuthenticated ||
      !connectionState.isConnected ||
      chatIds.length === 0
    ) {
      return;
    }

    // Set the current viewing chat in the cache manager only if this is the active tab
    if (!isInactiveTab) {
      setCurrentViewingChat(selectedChatId);
    }

    subscribeToChatsForList(chatIds);

    // Cleanup on unmount or when chat IDs change
    return () => {
      unsubscribeFromAllChatsForList();
    };
  }, [
    chatIds,
    isAuthenticated,
    connectionState.isConnected,
    selectedChatId,
    isInactiveTab,
  ]);

  // Keep the patient unread badge fresh: when any ping/chats list query
  // updates (e.g. realtime callback wrote to a chat in cache), invalidate
  // the dedicated badge count query so it refetches. The badge query is
  // also a ping/chats list — skip events for it to avoid an invalidation
  // loop. Was previously a client-side recompute over the visible list,
  // which broke under server-side filtering (ZCS-304).
  useEffect(() => {
    const badgeKey = JSON.stringify(
      queryKeys.ping.chats.list({ has_unread: true, page_size: 1 })
    );

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const queryKey = event?.query?.queryKey;
      if (
        queryKey?.[0] === "ping" &&
        queryKey?.[1] === "chats" &&
        event?.type === "updated" &&
        JSON.stringify(queryKey) !== badgeKey
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ping.chats.list({
            has_unread: true,
            page_size: 1,
          }),
          exact: true,
        });
      }
    });

    return unsubscribe;
  }, [queryClient]);

  // Force refresh of chat list data
  const refreshChatList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ping.chats.all(),
    });
  }, [queryClient]);

  // Update unread count for currently viewing chat (set to 0)
  const markCurrentChatAsRead = useCallback(() => {
    if (selectedChatId) {
      markChatAsReadInCache(queryClient, selectedChatId);
    }
  }, [queryClient, selectedChatId]);

  return {
    isConnected: connectionState.isConnected,
    connectionState,
    refreshChatList,
    markCurrentChatAsRead,
    activeSubscriptions: activeChatListSubscriptions.size,
  };
};
